import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Cloud,
  ThermometerSun,
  Mountain,
  Sun,
  CloudRain,
  CloudDrizzle,
  CloudSnow,
  Wind,
  CloudFog,
} from "lucide-react";
import MarketingShell from "../MarketingShell";
import SEOHead from "../../components/SEOHead";
import { useOpeningHours, useTourPricing, openingHoursSchema, formatPrice } from "../useBusinessInfo";
import "./home.css";

/**
 * RHomePage — redesigned homepage ("THE MUST-DO UNDERGROUND" poster concept).
 *
 * Visual ground truth: design-ref/public/"Homepage - Must-Do.html".
 * Functional ground truth: frontend/src/pages/HomePage.js — carried over:
 *   - SEOHead (same title/description/keywords/canonical/ogImage/schema)
 *   - live Coober Pedy weather (GET /api/weather/coober-pedy, 10-min refresh,
 *     fallback data + "Est." badge on error, condition→icon mapping, static
 *     underground 23.3°C card, BOM disclaimer) — restyled in the design system
 */

const baseSchema = {
  "@context": "https://schema.org",
  "@type": "TouristAttraction",
  name: "Umoona Opal Mine & Museum",
  description:
    "Underground opal mine tours and authentic Australian opal shopping in Coober Pedy. Experience the world's opal capital with guided mine tours and museum exhibits.",
  address: {
    "@type": "PostalAddress",
    streetAddress: "14 Hutchison St",
    addressLocality: "Coober Pedy",
    addressRegion: "SA",
    postalCode: "5723",
    addressCountry: "AU",
  },
  geo: {
    "@type": "GeoCoordinates",
    latitude: -29.0135,
    longitude: 134.753,
  },
  telephone: "+61886724588",
  url: "https://umoonaopalmine.com.au",
  priceRange: "$15-$50",
  hasMap: "https://goo.gl/maps/example",
  petsAllowed: false,
  amenityFeature: [
    {
      "@type": "LocationFeatureSpecification",
      name: "Pet Policy",
      value:
        "Dogs and pets are NOT permitted at Umoona Opal Mine & Museum for safety reasons",
    },
  ],
};

// Same condition→icon mapping as the legacy WeatherWidget, restyled.
const getWeatherIcon = (condition) => {
  if (!condition) return <Sun className="h-wxIcon h-wxIcon--sun" size={24} />;

  const conditionLower = condition.toLowerCase();

  if (conditionLower.includes("rain") || conditionLower.includes("shower")) {
    return <CloudRain className="h-wxIcon h-wxIcon--rain" size={24} />;
  } else if (conditionLower.includes("drizzle")) {
    return <CloudDrizzle className="h-wxIcon h-wxIcon--drizzle" size={24} />;
  } else if (conditionLower.includes("snow")) {
    return <CloudSnow className="h-wxIcon h-wxIcon--snow" size={24} />;
  } else if (
    conditionLower.includes("cloud") ||
    conditionLower.includes("overcast")
  ) {
    return <Cloud className="h-wxIcon h-wxIcon--cloud" size={24} />;
  } else if (conditionLower.includes("fog") || conditionLower.includes("mist")) {
    return <CloudFog className="h-wxIcon h-wxIcon--fog" size={24} />;
  } else if (conditionLower.includes("wind")) {
    return <Wind className="h-wxIcon h-wxIcon--wind" size={24} />;
  } else if (
    conditionLower.includes("clear") ||
    conditionLower.includes("sunny") ||
    conditionLower.includes("fine")
  ) {
    return <Sun className="h-wxIcon h-wxIcon--sun" size={24} />;
  }

  return <Sun className="h-wxIcon h-wxIcon--sun" size={24} />;
};

export default function RHomePage() {
  const hours = useOpeningHours();
  const pricing = useTourPricing();
  const schemaData = { ...baseSchema, ...openingHoursSchema(hours) };
  const [weather, setWeather] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const fetchWeather = async () => {
    try {
      setLoading(true);
      setError(false);

      // Fetch weather data from backend proxy (avoids CORS issues)
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/weather/coober-pedy`
      );

      if (!response.ok) {
        throw new Error("Failed to fetch weather data");
      }

      const data = await response.json();

      setWeather({
        temp: data.temp,
        apparentTemp: data.apparent_temp,
        condition: data.condition || "Clear",
        humidity: data.humidity,
        windSpeed: data.wind_speed,
        windDir: data.wind_dir,
        observationTime: data.observation_time,
        maxTemp: data.max_temp,
        minTemp: data.min_temp,
      });

      setLoading(false);
    } catch (err) {
      console.error("Error fetching weather:", err);
      setError(true);
      setLoading(false);
      // Set fallback data
      setWeather({
        temp: 25,
        condition: "Sunny",
        humidity: 30,
        windSpeed: 10,
      });
    }
  };

  useEffect(() => {
    fetchWeather();
    // Refresh weather every 10 minutes
    const interval = setInterval(fetchWeather, 600000);
    return () => clearInterval(interval);
  }, []);

  return (
    <MarketingShell pageClass="pg-home">
      <SEOHead
        title="Underground Opal Mine Tours & Authentic Australian Opals"
        description="Underground opal mine tours, a FREE museum and genuine Australian opals in Coober Pedy. Family-owned since the 1920s. Book your underground tour today!"
        keywords="opal mine tours, Coober Pedy tours, underground tours, Australian opals, opal shopping, mine museum, South Australia tourism, authentic opals, opal jewelry, underground experience"
        canonical="/"
        ogImage="/og-umoona.jpg"
        schemaData={schemaData}
      />

      {/* HERO · 100vh poster */}
      <div className="h-heroWrap">
        <div className="u-poster">
          <div className="h-posterShimmer" />
          <div className="h-posterScrim" />
          <div className="h-posterInner">
            <div className="h-posterEyebrow">
              The Opal Capital of the World · Est. 1920s
            </div>
            <h1 className="h-posterH1">
              THE
              <br />
              MUST-DO
            </h1>
            <div className="u-posterrow">
              <div className="h-underground">UNDERGROUND</div>
              <div className="u-freebadge">
                <div className="h-freeNum">FREE</div>
                <div className="h-freeCap">MUSEUM ENTRY</div>
              </div>
            </div>
            <div className="h-scrollcue">Scroll to descend into the mine ↓</div>
          </div>
        </div>
      </div>

      {/* PHOTO BAND + INTRO · entering the tunnel */}
      <div className="u-split">
        <div className="u-photoband">
          <img
            src="/design/assets/photo-minetour.jpg"
            alt="Guided underground mine tour"
            loading="lazy"
            decoding="async"
          />
        </div>
        <div className="h-intro">
          <p className="h-introLede">
            Hand-dug tunnels. A free museum. Genuine Australian opal —{" "}
            <span className="h-introAccent">straight from the miners.</span>
          </p>
          <p className="h-introBody">
            Step off the red dirt into the largest single underground tourist
            attraction in Coober Pedy. Family-owned and worked since the 1920s.
          </p>
          <div className="h-introBtns">
            <Link to="/book-tour" className="u-btn u-btn--primary">
              Book a Tour →
            </Link>
            <Link to="/free-museum" className="u-btn h-btnGhostPanel">
              Explore the Free Museum
            </Link>
          </div>
        </div>
      </div>

      {/* STAT BLOCKS · the opal seam glinting */}
      <div className="u-grid3 h-stats">
        <div className="h-stat h-stat--blue">
          <div className="h-statNum">100+</div>
          <div className="h-statCap">Years, since the 1920s</div>
        </div>
        <div className="h-stat h-stat--teal">
          <div className="h-statNum">70%</div>
          <div className="h-statCap">Of the world's opal</div>
        </div>
        <div className="h-stat h-stat--turq">
          <div className="h-statNum">22°C</div>
          <div className="h-statCap">Constant, all year</div>
        </div>
      </div>

      {/* LIVE WEATHER · carried over from the legacy homepage (WeatherWidget) */}
      <section className="u-sectpad h-weather">
        {loading && !weather ? (
          <div className="u-grid2 h-weatherGrid">
            <div className="h-weatherSkel" />
            <div className="h-weatherSkel" />
          </div>
        ) : (
          <>
            <div className="u-grid2 h-weatherGrid">
              {/* Coober Pedy current weather */}
              <div className="h-wxCard">
                <div className="h-wxHead">
                  {getWeatherIcon(weather?.condition)}
                  <span>Current Weather</span>
                  {error && <span className="h-wxEst">Est.</span>}
                </div>
                <div className="h-wxTitle">COOBER PEDY</div>
                <div className="h-wxCond">{weather?.condition || "Clear"}</div>
                <div className="h-wxTemp">
                  {weather?.temp ? Math.round(weather.temp) : "25"}
                  <span className="h-wxUnit">°C</span>
                </div>
                <div>
                  {weather?.apparentTemp && (
                    <p className="h-wxFeels">
                      Feels like {Math.round(weather.apparentTemp)}°C
                    </p>
                  )}
                  {weather?.observationTime && (
                    <p className="h-wxObs">
                      Measured at {weather.observationTime}
                    </p>
                  )}
                </div>
                <div className="h-wxMeta">
                  {weather?.humidity && (
                    <div>
                      <span>Humidity</span>
                      <strong>{weather.humidity}%</strong>
                    </div>
                  )}
                  {weather?.windSpeed !== undefined && (
                    <div>
                      <span>Wind</span>
                      <strong>
                        {weather.windSpeed} km/h {weather?.windDir || ""}
                      </strong>
                    </div>
                  )}
                </div>
              </div>

              {/* Underground mine temperature — static */}
              <div className="h-wxCard h-wxCard--mine">
                <div className="h-wxHead">
                  <Mountain className="h-wxIcon" size={24} />
                  <span>Underground</span>
                  <ThermometerSun className="h-wxIcon" size={22} />
                </div>
                <div className="h-wxTitle">Inside Umoona Opal Mine</div>
                <div className="h-wxCond">Year-round constant</div>
                <div className="h-wxTemp">
                  23.3<span className="h-wxUnit">°C</span>
                </div>
                <p className="h-wxNote">
                  Perfect climate underground - naturally cool and comfortable
                  all year
                </p>
              </div>
            </div>
            {!error && weather?.observationTime && (
              <p className="h-wxDisclaimer">
                Weather data from Bureau of Meteorology • Updated regularly
              </p>
            )}
          </>
        )}
      </section>

      {/* COMPLETE EXPERIENCE · deeper into the rock */}
      <section className="u-sectpad u-sect-deep h-experience">
        <div className="u-eyebrow">The complete Coober Pedy experience</div>
        <h2 className="u-h2">Everything, one iconic stop</h2>
        <div className="u-grid3">
          <Link to="/underground-tours" className="u-card">
            <div className="u-card__media">
              <img src="/design/assets/photo-minetour.jpg" alt="Guided underground opal mine tour at Umoona, Coober Pedy" loading="lazy" decoding="async" />
            </div>
            <div className="u-card__body">
              <div className="u-card__kicker">FROM {formatPrice(Math.min(pricing.adult, pricing.child))} · DAILY</div>
              <div className="u-card__title">Guided Mine Tours</div>
              <p className="u-card__text">
                Walk original workings with expert local guides. Tours at 10am,
                2pm &amp; 4pm.
              </p>
            </div>
          </Link>
          <Link to="/free-museum" className="u-card">
            <div className="u-card__media">
              <img src="/design/assets/photo-showroom.jpg" alt="Mining heritage displays inside Umoona's free underground museum" loading="lazy" decoding="async" />
            </div>
            <div className="u-card__body">
              <div className="u-card__kicker h-kickTeal">
                FREE ENTRY · ALL AGES
              </div>
              <div className="u-card__title">The Free Museum</div>
              <p className="u-card__text">
                Mining history, Aboriginal culture &amp; the award-winning Story
                of Opal cinema.
              </p>
            </div>
          </Link>
          <Link to="/opal-store" className="u-card">
            <div className="u-card__media">
              <img src="/design/assets/photo-opal.jpg" alt="Polished authentic Coober Pedy opal with vivid play-of-colour" loading="lazy" decoding="async" />
            </div>
            <div className="u-card__body">
              <div className="u-card__kicker h-kickTurq">
                DIRECT FROM THE MINERS
              </div>
              <div className="u-card__title">The Opal Store</div>
              <p className="u-card__text">
                Authentic Coober Pedy opal with a certificate of authenticity on
                every piece.
              </p>
            </div>
          </Link>
        </div>
        <div className="u-grid2 h-banners">
          <Link to="/webcam" className="h-banner h-banner--blue">
            <div>
              <div className="h-bannerKick">● LIVE 24/7</div>
              <div className="h-bannerTitle">Live Coober Pedy Webcams</div>
              <p className="h-bannerText">
                Watch Hutchison Street &amp; the opal fields from our rooftop.
              </p>
            </div>
            <span className="h-bannerArrow">→</span>
          </Link>
          <Link
            to="/group-accommodation"
            className="h-banner h-banner--teal"
          >
            <div>
              <div className="h-bannerKick">102 BEDS · 24°C</div>
              <div className="h-bannerTitle">Groups &amp; Underground Stays</div>
              <p className="h-bannerText">
                Custom school &amp; tour-group experiences and bunkhouse
                accommodation.
              </p>
            </div>
            <span className="h-bannerArrow">→</span>
          </Link>
        </div>
      </section>

      {/* TESTIMONIALS · deep */}
      <section className="u-sectpad u-sect-test h-test">
        <div className="h-testHead">
          <span className="h-testEyebrow">★★★★★ · Google Reviews</span>
        </div>
        <div className="u-grid3">
          <div className="h-quote">
            <div className="h-quoteStars">★★★★★</div>
            <p className="h-quoteText">
              "A brilliant tour led by Zoe, a local — a must in Coober Pedy."
            </p>
            <div className="h-quoteName">Daryl Scales</div>
          </div>
          <div className="h-quote">
            <div className="h-quoteStars">★★★★★</div>
            <p className="h-quoteText">
              "Beautiful museum with history of the area, its people, fossils
              and opals. Excellent shop as well."
            </p>
            <div className="h-quoteName">Liv Rainey-Smith</div>
          </div>
          <div className="h-quote">
            <div className="h-quoteStars">★★★★★</div>
            <p className="h-quoteText">
              "We had a lovely time with the guide. All staff were friendly and
              helpful. Great place to visit when in Coober Pedy."
            </p>
            <div className="h-quoteName">Ayoma Dhanapala</div>
          </div>
        </div>
      </section>

      {/* TICKET CTA · the seam floor */}
      <section className="u-ticketcta u-sectpad">
        <div>
          <div className="u-ticketcta__title">
            Daily tours · 10am, 2pm &amp; 4pm
          </div>
          <div className="u-ticketcta__sub">
            ADULTS {formatPrice(pricing.adult)} · CHILDREN {formatPrice(pricing.child)} · UNDER 5 FREE
          </div>
        </div>
        <Link to="/book-tour" className="u-btn u-btn--ticket">
          Get Tickets
        </Link>
      </section>
    </MarketingShell>
  );
}
